import React, { useContext, useMemo } from 'react';
import {
  DndContext, MouseSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors, closestCenter, DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import type { BorderCondition } from 'dorian-shared/types/branch/HubOption';
import { HUB_MAX_BORDER_CONDITIONS } from 'dorian-shared/types/branch/HubOption';
import { MemoryDTO, MemoryType } from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { NodeEditContext } from '../../../../context/NodeEditContext';
import { BorderConditionCard } from './BorderConditionCard';
import { isBorderConditionValid } from '../../borderConditionValidation';
import './BorderOverrides.scss';

const MOUSE_SENSOR_OPTIONS = { activationConstraint: { distance: 5 } };
const KEYBOARD_SENSOR_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };

interface BorderOverridesProps {
  optionUuid: string;
  conditions: BorderCondition[];
  isDisabled: boolean;
  onDragEnd: (event: DragEndEvent) => void;
  onAdd: () => void;
  onRemove: (uuid: string) => void;
  onUpdate: (uuid: string, updated: BorderCondition) => void;
}

export function BorderOverrides({
  optionUuid, conditions, isDisabled, onDragEnd, onAdd, onRemove, onUpdate,
}: BorderOverridesProps) {
  const ctx = useContext(NodeEditContext) as Record<string, unknown> | undefined;
  const rawSlots = ctx?.memoryBankSlots;
  const memoryBankSlots = Array.isArray(rawSlots) ? (rawSlots as MemoryDTO[]) : null;
  const memoryVariables = useMemo(
    () => (memoryBankSlots ?? []).filter((s: MemoryDTO) => s.type === MemoryType.Number),
    [memoryBankSlots],
  );

  const sortableItems = useMemo(() => conditions.map((c) => c.uuid), [conditions]);

  const sensors = useSensors(
    useSensor(MouseSensor, MOUSE_SENSOR_OPTIONS),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS),
  );

  const atLimit = conditions.length >= HUB_MAX_BORDER_CONDITIONS;

  return (
    <div className="border-overrides" data-testid="border-overrides">
      <p className="border-overrides__helper">
        First match overrides the default border color.
      </p>

      <DndContext
        id={`border-dnd-${optionUuid}`}
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={onDragEnd}
      >
        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
          <div className="list-group mb-1">
            {conditions.map((cond) => (
              <BorderConditionCard
                key={cond.uuid}
                condition={cond}
                memoryVariables={memoryVariables}
                isDisabled={isDisabled}
                isValid={isBorderConditionValid(cond)}
                onChange={onUpdate}
                onRemove={onRemove}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="border-overrides__otherwise">
        <strong className="small mr-2">Otherwise</strong>
        <small className="text-muted">uses default border above</small>
      </div>

      <div className="d-flex align-items-center mt-1">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={isDisabled || atLimit}
          onClick={onAdd}
          data-testid="border-overrides-add"
        >
          + Add Border Override
        </button>
        <small className="text-muted ml-2">
          {conditions.length} / {HUB_MAX_BORDER_CONDITIONS}
        </small>
      </div>
    </div>
  );
}
